import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import apiRequest from "../../utils/apiRequest";
import RemixModalLoader from "../remix/shared/RemixModalLoader";
import RemixModalComplete from "../remix/shared/RemixModalComplete";
import useActionCable from "../shared/useActionCable";
import Tooltip from "../shared/Tooltip";
import CFFontFamilySelect from "../shared/forms/CFFontFamilySelect";
import CFColorPicker from "../shared/forms/CFColorPicker";
import {
  canUseAiRemixTemplate,
  remixAiUpgradePlan,
} from "../../utils/canUseAiRemixText";

// iOS-style toggle switch component matching the existing app styling
const ToggleSwitch = ({ checked, onChange }) => {
  const trackStyles = {
    width: "40px",
    height: "20px",
    backgroundColor: checked ? "#003FFF" : "#CBD4E1",
    borderRadius: "10px",
    position: "relative",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    marginTop: "5px",
  };

  const knobStyles = {
    width: "18px",
    height: "18px",
    backgroundColor: "#ffffff",
    borderRadius: "50%",
    position: "absolute",
    top: "1px",
    left: checked ? "21px" : "1px",
    transition: "left 0.2s ease",
    boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.3)",
  };

  return (
    <div style={trackStyles} onClick={onChange}>
      <div style={knobStyles}></div>
    </div>
  );
};

const CreateVariantFromTemplateModal = ({
  isOpen,
  onClose,
  templateData,
  website,
  cta,
  createPath,
  onSuccess,
  currentUser,
}) => {
  const [variantName, setVariantName] = useState("");
  const [remixStyles, setRemixStyles] = useState(false);
  const [remixAllText, setRemixAllText] = useState(false);
  const [remixInstructions, setRemixInstructions] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cableSyncId, setCableSyncId] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [redirectUrl, setRedirectUrl] = useState(null);

  // Remix styling options with defaults from website brand settings
  const [headingFontFamily, setHeadingFontFamily] = useState(
    website?.data?.brand_font_headline || ""
  );
  const [headingFontColor, setHeadingFontColor] = useState("#000");
  const [paragraphFontFamily, setParagraphFontFamily] = useState(
    website?.data?.brand_font_paragraph || ""
  );
  const [paragraphFontColor, setParagraphFontColor] = useState("#000");
  const [backgroundColor, setBackgroundColor] = useState("#fff");
  const [buttonColor, setButtonColor] = useState(
    website?.data?.brand_color_vibrant || "#000"
  );
  const [accentColor, setAccentColor] = useState(
    website?.data?.brand_color_dark || "#000"
  );

  const modalRef = useRef(null);
  const isMounted = useRef(false);

  const { lastMessage: progress } = useActionCable(
    isLoading ? "ProgressValueChannel" : null,
    { id: cableSyncId },
    {
      autoDisconnect: true,
      autoDisconnectCondition: (value) => value === 100,
    }
  );

  useActionCable(
    isLoading ? "AsyncModalRequestConfirmationChannel" : null,
    { id: cableSyncId },
    {
      onReceived: (data) => {
        switch (data?.status) {
          case "ready":
            handleSuccess(data);
            break;
          case "failed":
            console.error(
              "CreateVariantFromTemplateModal - Request failed:",
              data.error
            );
            setIsLoading(false);
            setStatusMessage("Failed to create variant. Please try again.");
            break;
          case "rate_limited":
            setStatusMessage(
              data.message || "Rate limited. Please try again later."
            );
            break;
          default:
            console.warn(
              "CreateVariantFromTemplateModal - Unknown status:",
              data
            );
        }
      },
      autoDisconnect: true,
      autoDisconnectCondition: (data) =>
        data?.status === "ready" || data?.status === "failed",
    }
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setVariantName("");
      setStatusMessage("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && modalRef.current && window.$ && window.$.fn.modal) {
      const $modal = window.$(modalRef.current);
      $modal.modal("show");

      // Close the New Variant modal when opening this one (template preview's hidden handler re-shows it)
      const closeNewVariantTimer = setTimeout(() => {
        window.$("#newVariant").modal("hide");
      }, 400);

      const handleHidden = () => {
        if (isMounted.current) {
          onClose();
        }
      };

      $modal.on("hidden.bs.modal", handleHidden);

      return () => {
        clearTimeout(closeNewVariantTimer);
        $modal.off("hidden.bs.modal", handleHidden);
      };
    }
  }, [isOpen, onClose]);

  const handleSuccess = (data) => {
    if (!isMounted.current) return;

    if (onSuccess && typeof onSuccess === "function") {
      setIsLoading(false);
      onSuccess(data);
      return;
    }

    const newVariant = data?.object;
    const ctaId = newVariant?.cta_id ?? cta?.id;
    if (newVariant?.id && ctaId && website?.id) {
      const url = `/websites/${website.id}/cta/${ctaId}/variants/${newVariant.id}/edit`;
      // Show the complete state, then navigate immediately.
      setRedirectUrl(url);
      window.location.href = url;
    } else {
      setIsLoading(false);
      setStatusMessage(
        "Variant created but redirect failed. Please navigate manually."
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading || !templateData) return;

    const newSyncId = Math.random().toString(36).substring(2, 15);
    setCableSyncId(newSyncId);
    setIsLoading(true);
    setStatusMessage("Creating variant...");

    try {
      const remixParams = {
        remix_styles: remixStyles ? "true" : "false",
        remix_instructions: remixInstructions,
        remix_all_text: remixAllText ? "true" : "false",
      };

      if (remixStyles) {
        remixParams["heading-font-family"] = headingFontFamily;
        remixParams["heading-font-color"] = headingFontColor;
        remixParams["paragraph-font-family"] = paragraphFontFamily;
        remixParams["paragraph-font-color"] = paragraphFontColor;
        remixParams["background-color"] = backgroundColor;
        remixParams["button-color"] = buttonColor;
        remixParams["accent-color"] = accentColor;
      }

      await apiRequest("POST", createPath, {
        variant: {
          template: templateData.object.id,
          cta_id: cta.id,
        },
        website_id: website.id,
        variant_name: variantName,
        cable_sync_id: newSyncId,
        remix: remixParams,
      });
    } catch (error) {
      console.error("Error creating variant from template:", error);
      setIsLoading(false);
      setStatusMessage("Failed to start. Please try again.");
    }
  };

  const handleClose = () => {
    if (isLoading) return;

    if (modalRef.current && window.$ && window.$.fn.modal) {
      window.$(modalRef.current).modal("hide");
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape" && !isLoading) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isLoading]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={modalRef}
      className="modal"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="createVariantFromTemplateModalLabel"
      style={{ display: "block", background: "rgba(0, 0, 0, 0.5)" }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          display: "flex",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleBackdropClick}
      >
        <div
          className="modal-dialog !max-w-[1600px]"
          role="document"
          style={{ width: "400px" }}
        >
          <div className="modal-content smooth">
            <div className="modal-header">
              <button
                type="button"
                className="close"
                onClick={handleClose}
                aria-label="Close"
                disabled={isLoading}
                data-dismiss="modal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
                >
                  <path
                    d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <h4 className="modal-title text-[18px] font-semibold px-5">
                Create Variant from Template
              </h4>
            </div>

            {redirectUrl ? (
              <div className="modal-body p-6">
                <RemixModalComplete redirectUrl={redirectUrl} />
              </div>
            ) : !isLoading ? (
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {statusMessage && (
                    <div
                      className="col-xs-12 p-4 mb-4 rounded-md bg-amber-50 border border-amber-200 text-amber-800"
                      role="alert"
                    >
                      {statusMessage}
                    </div>
                  )}
                  <div
                    className="row hard-center some-vertical-space"
                    style={{ marginTop: "0px" }}
                  >
                    {/* Variant Name */}
                    <div className="col-xs-12 some-vertical-space">
                      <span className="text-xl text-black">Variant name</span>
                      <input
                        type="text"
                        className="w-full text-xl border-solid border-[1px] border-slate-200 rounded-md text-black p-4"
                        value={variantName}
                        onChange={(e) => setVariantName(e.target.value)}
                        placeholder="Name your split-test"
                        required
                        autoFocus
                      />
                    </div>

                    {/* Remix Styling Toggle */}
                    <div
                      className="col-xs-6 form-inputs mb-10"
                      style={{ paddingRight: "5px" }}
                    >
                      <span className="field-title text-xl">
                        Remix style{" "}
                        <Tooltip
                          tooltip="Apply your brand styles (fonts, colors) to the template."
                          inline={true}
                        />
                      </span>

                      <ToggleSwitch
                        checked={remixStyles}
                        onChange={() => setRemixStyles(!remixStyles)}
                      />
                    </div>

                    {/* AI Remix All Text Toggle */}
                    <div
                      className="col-xs-6 form-inputs mb-10"
                      style={{ paddingRight: "5px" }}
                    >
                      <span className="field-title text-xl block">
                        Remix all text{" "}
                        <span className="relative ml-2 top-[-3px] bg-blue-600 py-1 px-2.5 rounded text-sm text-white font-medium">
                          BETA
                        </span>{" "}
                        <Tooltip
                          tooltip="Use AI to rewrite all text in the variant based on your instructions."
                          inline={true}
                        />
                      </span>

                      <div className="mt-2 row hard-center pl-[20px]">
                        <ToggleSwitch
                          checked={remixAllText}
                          onChange={(e) => {
                            const next = !remixAllText;
                            if (
                              next &&
                              !canUseAiRemixTemplate({
                                currentUser,
                                website,
                                template: templateData?.template,
                              })
                            ) {
                              if (
                                typeof window.subscriptions?.upgrade_modal ===
                                "function"
                              ) {
                                window.subscriptions.upgrade_modal(
                                  e?.nativeEvent ?? e ?? null,
                                  false,
                                  "use remix AI to generate a template",
                                  remixAiUpgradePlan({ website }),
                                  "Triggered feature CTA – Remix AI",
                                  false,
                                  "x3rs6cvrwi"
                                );
                              }
                              return;
                            }
                            setRemixAllText(next);
                          }}
                        />
                      </div>
                      {remixAllText &&
                        Number(
                          templateData?.template?.ai_remix_actions_estimate
                        ) > 0 && (
                          <p className="mt-1.5 mb-0 text-sm text-gray-500 leading-snug pl-[20px]">
                            Uses about{" "}
                            {templateData.template.ai_remix_actions_estimate}{" "}
                            actions
                          </p>
                        )}
                    </div>

                    {/* Copywriting AI Remix Instructions */}
                    {remixAllText && (
                      <div className="col-xs-12 form-inputs">
                        <span className="field-title text-xl">
                          Copywriting AI Remix Instructions{" "}
                          <Tooltip
                            tooltip="Describe the tone for this variant for remixing text with AI."
                            inline={true}
                          />
                        </span>

                        <textarea
                          rows={6}
                          className="w-full text-xl border-solid border-[1px] border-slate-200 rounded-md text-black p-4 resize-y min-h-[6rem]"
                          value={remixInstructions}
                          onChange={(e) => setRemixInstructions(e.target.value)}
                          placeholder="Urgency for Black Friday/Cyber Monday."
                        />
                      </div>
                    )}

                    {/* Remix Styling Settings */}
                    {remixStyles && (
                      <>
                        {/* Heading Font Row */}
                        <div
                          className="col-xs-12 no-padding-left mt-5"
                          style={{ paddingRight: "10px" }}
                        >
                          <div className="row hard-center">
                            <div
                              className="col-xs-8 form-inputs"
                              style={{ paddingRight: "5px" }}
                            >
                              <span className="field-title text-xl">
                                Heading Font{" "}
                                <Tooltip
                                  tooltip="All headings will use this font by default."
                                  inline={true}
                                />
                              </span>

                              <CFFontFamilySelect
                                name="remix[heading-font-family]"
                                placeholder="Select a font"
                                website={website}
                                value={headingFontFamily}
                                callback={(value) =>
                                  setHeadingFontFamily(value)
                                }
                              />
                            </div>

                            <div
                              className="col-xs-4 form-inputs color-field"
                              style={{ paddingRight: "5px" }}
                            >
                              <span className="field-title text-xl inline">
                                Color
                              </span>

                              <CFColorPicker
                                name="remix[heading-font-color]"
                                placeholder="#000000"
                                website={website}
                                value={headingFontColor}
                                onChange={(value) => setHeadingFontColor(value)}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Text Font Row */}
                        <div
                          className="col-xs-12 no-padding-left mt-5"
                          style={{ paddingRight: "10px" }}
                        >
                          <div className="row hard-center">
                            <div
                              className="col-xs-8 form-inputs"
                              style={{ paddingRight: "5px" }}
                            >
                              <span className="field-title text-xl">
                                Text Font{" "}
                                <Tooltip
                                  tooltip="All paragraph text will use this font by default."
                                  inline={true}
                                />
                              </span>

                              <CFFontFamilySelect
                                name="remix[paragraph-font-family]"
                                placeholder="Select a font"
                                website={website}
                                value={paragraphFontFamily}
                                callback={(value) =>
                                  setParagraphFontFamily(value)
                                }
                              />
                            </div>

                            <div
                              className="col-xs-4 form-inputs color-field"
                              style={{ paddingRight: "5px" }}
                            >
                              <span className="field-title text-xl inline">
                                Color
                              </span>

                              <CFColorPicker
                                name="remix[paragraph-font-color]"
                                placeholder="#000000"
                                website={website}
                                value={paragraphFontColor}
                                onChange={(value) =>
                                  setParagraphFontColor(value)
                                }
                              />
                            </div>
                          </div>
                        </div>

                        {/* Colors Row */}
                        <div
                          className="col-xs-12 no-padding-left mt-5 mb-5"
                          style={{ paddingRight: "10px" }}
                        >
                          <div className="row hard-center">
                            <div
                              className="col-xs-4 form-inputs color-field"
                              style={{ paddingRight: "5px" }}
                            >
                              <span className="field-title text-xl inline">
                                Background
                              </span>

                              <CFColorPicker
                                name="remix[background-color]"
                                placeholder="#fff"
                                website={website}
                                value={backgroundColor}
                                onChange={(value) => setBackgroundColor(value)}
                              />
                            </div>

                            <div
                              className="col-xs-4 form-inputs color-field"
                              style={{ paddingRight: "5px" }}
                            >
                              <span className="field-title text-xl inline">
                                Buttons
                              </span>

                              <CFColorPicker
                                name="remix[button-color]"
                                placeholder="#003FFF"
                                website={website}
                                value={buttonColor}
                                onChange={(value) => setButtonColor(value)}
                              />
                            </div>

                            <div
                              className="col-xs-4 form-inputs color-field"
                              style={{ paddingRight: "5px" }}
                            >
                              <span className="field-title text-xl inline">
                                Accents{" "}
                                <Tooltip
                                  tooltip="This color will be used for various elements with color accents."
                                  inline={true}
                                />
                              </span>

                              <CFColorPicker
                                name="remix[accent-color]"
                                placeholder="#000000"
                                website={website}
                                value={accentColor}
                                onChange={(value) => setAccentColor(value)}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Submit Button */}
                    <div className="col-xs-12 mt-5">
                      <button
                        type="submit"
                        className="w-full text-white text-xl font-semibold rounded-md p-4 border-0"
                        style={{
                          background: remixAllText
                            ? "linear-gradient(135deg, #4f6fff 0%, #003FFF 100%)"
                            : "linear-gradient(90deg, #003FFF 0%, #2563eb 100%)",
                        }}
                        disabled={remixAllText && !remixInstructions.trim()}
                      >
                        {remixAllText ? "Remix Template" : "Create Variant +"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <div className="modal-body p-6">
                <RemixModalLoader
                  statusMessage={statusMessage}
                  progress={progress}
                  showAiGeneratingStep={remixAllText}
                  bottomMessage={
                    remixAllText
                      ? "AI is rewriting your content. This may take a moment..."
                      : "This may take 30-60 seconds. We're doing real research."
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateVariantFromTemplateModal;
